const content = `
# ADMM Coordination

**ADMM** (Alternating Direction Method of Multipliers) is the distributed optimization algorithm used to coordinate multiple assets (battery, EV chargers, COLD storage) for peak demand management.

## Why Distributed?

Each asset type has different constraints and optimization models:
- **Battery**: LP with SOC limits and C-rate constraints
- **EV Chargers**: MILP with vehicle arrival/departure windows and energy targets
- **COLD Storage**: MILP with temperature limits and compressor staging

A single monolithic optimizer would need to handle all constraint types simultaneously. ADMM lets each agent solve its own subproblem while a coordinator ensures they agree on the shared power profile.

## Consensus ADMM

Flux PhD uses **consensus ADMM**, where agents must agree on a shared target power profile:

\`\`\`
For each iteration k:
  1. Each agent i solves: minimize cost_i(x_i) + (rho/2) * ||x_i - z + u_i||^2
  2. Coordinator updates: z = average(x_i + u_i) projected onto peak constraint
  3. Each agent updates: u_i = u_i + (x_i - z)
\`\`\`

Where:
- **x_i** -- Agent i's power schedule
- **z** -- Shared consensus target (what everyone should do)
- **u_i** -- Dual variable (penalty for agent i deviating from consensus)
- **rho** -- Penalty parameter (controls convergence speed vs. oscillation)

## Day-Boundary ADMM

ADMM runs at **day boundaries only**, not every 15-minute timestep. This is because:
- EV and COLD MILP solves are expensive (seconds each)
- Running ADMM every 15 min would require thousands of MILPs per day
- Day-boundary coordination plus intra-day greedy dispatch achieves 85%+ PoP

Within each day, agents follow their ADMM-planned schedules with minor adjustments for real-time deviations.

## Convergence

ADMM converges when the **primal residual** (how much agents disagree with the target) and **dual residual** (how much the target is changing) both drop below tolerance.

Typical convergence: 10-30 iterations per day, each iteration taking ~1 second for the full agent ensemble.

## Key Design Decisions

| Decision | Rationale |
|----------|-----------|
| Fresh start per day | Rolling duals from day N to N+1 causes divergence (stale duals) |
| Z-seed from baseline | Initialize target from uncoordinated proposals, then project |
| Battery-only intra-day | Only battery can adjust in real-time (LP is fast); EV/COLD follow plan |
| First-day do-no-harm | On day 1, ensure orchestration does not increase peak above uncoordinated baseline |

## Relationship to Column Generation

Column Generation (CG) is an alternative coordination algorithm being developed. Where ADMM iterates on price signals, CG generates columns (candidate schedules) and uses a restricted master problem (RMP) to blend them. CG can find tighter solutions but is more complex.
`;

export default content;
